<template>
  <div class="job-filter-editor-page" v-rect-loading="loading">
    <div class="job-filter-editor-header">
      <div class="job-filter-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedForm
        ref="form"
        class="job-filter-edit-form"
        label-width="220px"
        :model="formData"
        :formLines="formLines"
      >
      </WrappedForm>
      <TableList
        v-if="hasOption"
        row-key="id"
        class="custom-job-filter-list"
        :createBtnLabelI18nKey="`${i18nPrefix}.label.addOption`"
        :listData="optionList"
        :tableColumns="tableColumns"
        :actionBtn="actionBtn"
        @create="handleCreateOption"
      >
        <template #draggable>
          <div class="drag-btn">
          <i class="iconfont iconMore draggable-handle"></i>
          </div>
        </template>
      </TableList>
      <div class="bottom-btn-group" :class="!hasOption ? 'no-option' : ''">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" :disabled="disabledSave" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
    <DialogWrapper
      saveBtnLabelI18nKey="common.btn.confirm"
      closeOnCancel
      :title="optionDialog.title"
      :show.sync="optionDialog.show"
      @save="handleSaveOption"
    >
      <WrappedForm
        ref="optionForm"
        class="option-edit-form"
        label-width="220px"
        :model="optionData"
        :formLines="optionFormLines"
      >
      </WrappedForm>
    </DialogWrapper>
    <DialogWrapper
      saveBtnLabelI18nKey="common.btn.confirm"
      closeOnCancel
      :title="deleteDialog.title"
      :show.sync="deleteDialog.show"
      @save="handleDelete"
    >
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { mapActions } from 'vuex'
import { addI18nProperty, selectLabelI18n } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'
import { CUSTOM_FIELD_TYPE } from '@/constant/job'

export default {
  components: {
    WrappedForm,
    TableList,
    DialogWrapper
  },
  props: {
  },
  data () {
    return {
      CUSTOM_FIELD_TYPE,
      loading: false,
      i18nPrefix: 'admin.company.customJobFilter',
      candidateStatusList: [],
      formData: {
        name: null,
        cn_name: null,
        visible_only_member: false,
        type: CUSTOM_FIELD_TYPE.SELECT
      },
      optionList: [],
      optionData: {},
      optionDialog: {
        show: false,
        title: null,
        editIndex: null
      },
      deleteDialog: {
        show: false,
        title: null,
        deleteIndex: null
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    filterId () {
      return this.$route.params.filterId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.label.editJobFilter' : '.label.addJobFilter'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    form () {
      return this.$refs.form
    },
    actionBtn () {
      return {
        edit: {
          show: true,
          labelI18nKey: 'common.btn.edit',
          onclick: this.handleEditOption
        },
        delete: {
          show: true,
          labelI18nKey: 'common.btn.delete',
          onclick: this.handleDeleteOption
        }
      }
    },
    hasOptionFilterType () {
      return [this.CUSTOM_FIELD_TYPE.SINGLE, this.CUSTOM_FIELD_TYPE.SELECT]
    },
    optionTypeList () {
      return [
        {
          key: this.CUSTOM_FIELD_TYPE.SELECT,
          label: this.$t(`${this.i18nPrefix}.label.select`)
        },
        {
          key: this.CUSTOM_FIELD_TYPE.SINGLE,
          label: this.$t(`${this.i18nPrefix}.label.single`)
        },
        {
          key: this.CUSTOM_FIELD_TYPE.TEXT,
          label: this.$t(`${this.i18nPrefix}.label.text`)
        },
        {
          key: this.CUSTOM_FIELD_TYPE.TEXTAREA,
          label: this.$t(`${this.i18nPrefix}.label.textarea`)
        },
        {
          key: this.CUSTOM_FIELD_TYPE.DATE,
          label: this.$t(`${this.i18nPrefix}.label.date`)
        }
      ]
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        cn_name: {
          label: this.$t(`${this.i18nPrefix}.label.cnFieldLabel`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.cnFieldLabel`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.enFieldLabel`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.enFieldLabel`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        type: {
          elType: 'el-select',
          disabled: this.isEditMode,
          option: {
            data: this.optionTypeList,
            labelKey: 'label',
            valueKey: 'key'
          },
          label: this.$t(`${this.i18nPrefix}.label.fieldType`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.fieldType`),
          rules: [{ required: true, message: requiredMessage, trigger: 'change' }]
        },
        visible_only_member: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.visibleOnlyMember`),
          option: {
            data: [{ label: this.$t('common.label.yes'), value: true }, { label: this.$t('common.label.no'), value: false }]
          }
        }
      }
      return lines
    },
    optionFormLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        cn_name: {
          label: this.$t(`${this.i18nPrefix}.label.cnFieldValue`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.cnFieldValue`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.enFieldValue`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.enFieldValue`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        }
      }
      return lines
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.cnFieldValue`),
          dataKey: 'cn_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.enFieldValue`),
          dataKey: 'name'
        },
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: 50
        }
      ]
      return columns
    },
    hasOption () {
      return this.hasOptionFilterType.includes(this.formData.type)
    },
    disabledSave () {
      const length = this.optionList?.length
      if (this.hasOption && (!length || length === 0)) {
        return true
      }
      return false
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('company', [
      'saveJobFilter',
      'updateJobFilter',
      'getJobFilterItemBinding'
    ]),
    async initData () {
      if (this.isEditMode && this.filterId) {
        this.loading = true
        const params = {
          filterId: this.filterId
        }
        try {
          const resp = await this.getJobFilterItemBinding(params)
          if (resp) {
            this.formData = resp
            this.optionList = resp.filter_item_list
          }
        } finally {
          this.loading = false
        }
      }
    },
    async loadCandidateStatusChangeCause () {
      const params = {
        causeId: this.causeId,
        params: {
        }
      }
      const resp = await this.getCandidateStatusChangeCauseByCauseId(params)
      if (resp) {
        this.formData = {
          status: resp.status.key,
          cause_cn: resp.cause_cn,
          cause_en: resp.cause_en
        }
      }
    },
    async loadCandidateStatus () {
      const resp = await this.getCandidateStatusList()
      if (resp) {
        _.forEach(resp, candidateStatus => {
          addI18nProperty(candidateStatus, 'name', 'name_cn', 'name_en')
        })
        this.candidateStatusList = resp
      }
    },
    handleCreateOption () {
      this.optionDialog.show = true
      this.optionDialog.title = this.$t(`${this.i18nPrefix}.label.addOption`)
      this.optionDialog.editIndex = null
      this.optionData = {}
    },
    handleEditOption (row, index) {
      this.optionDialog.show = true
      this.optionDialog.title = this.$t(`${this.i18nPrefix}.label.editOption`)
      this.optionDialog.editIndex = index
      this.optionData = { ...row }
    },
    handleDeleteOption (row, index) {
      this.deleteDialog.show = true
      this.deleteDialog.title = this.$t(`${this.i18nPrefix}.message.deleteOption`, { name: row.name, cnName: row.cn_name })
      this.deleteDialog.deleteIndex = index
    },
    async handleSaveOption () {
      const form = this.$refs.optionForm
      let result = false
      result = await form.submit()
      if (!result) {
        return
      }
      const editIndex = this.optionDialog.editIndex
      if (_.isNull(editIndex)) {
        this.optionList.push({ ...this.optionData })
      } else {
        this.optionList.splice(editIndex, 1, { ...this.optionData })
      }
      this.optionData = {}
      this.optionDialog.show = false
    },
    handleDelete () {
      this.optionList.splice(this.deleteDialog.deleteIndex, 1)
      this.deleteDialog.show = false
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        const length = this.optionList.length
        let filterItemList = null
        if (this.hasOption) {
          _.forEach(this.optionList, (option, index) => {
            option.order = length - index
          })
          filterItemList = this.optionList
        }
        let data = {
          ...this.formData,
          filter_item_list: filterItemList
        }
        let saveFunc = this.saveJobFilter
        if (this.isEditMode) {
          data = {
            filterId: this.filterId,
            data: data
          }
          saveFunc = this.updateJobFilter
        }
        const resp = await saveFunc(data)
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.$router.push('/v-admin/custom-job-filter/management/list')
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-filter-editor-page {
  .job-filter-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .job-filter-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    margin: 30px auto 0;

    .job-filter-edit-form {
      width: 800px;
    }

    .custom-job-filter-list {
      margin-bottom: 30px;

      .drag-btn {
        .draggable-handle {
          cursor: pointer;
        }
      }
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    &.no-option {
      width: 800px;
      margin: 0 auto;
    }

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
